import type { UseMutationOptions } from '@tanstack/react-query';
import { type EvidenceUploadMode, type ProcessUploadResponse, type UploadEvidenceData, useUploadEvidence } from 'api';
import { logger } from 'monitor';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useOfflineEvidenceStore } from '../stores/useOfflineEvidenceStore';
import { extractExifData } from '../utils/exif/exif';

export interface OfflinePhoto {
  id: string;
  url: string;
}

export interface OfflineProcessUploadResponse {
  created: string;
  id: string;
  image: string;
  message: string;
  status: 'pending' | 'success' | 'error';
}

interface SyncResult {
  offlineId: string;
  success: boolean;
  result?: ProcessUploadResponse;
  error?: Error;
}

interface SyncComplete {
  message: string;
  results: SyncResult[];
  success: boolean;
}

export const useOfflineEvidence = ({
  parentId,
  mode,
  isOnline,
  mutationOptions,
  onSyncComplete,
}: {
  parentId: string | number;
  mode: EvidenceUploadMode;
  isOnline: boolean;
  mutationOptions?: UseMutationOptions<ProcessUploadResponse, unknown, UploadEvidenceData, unknown>;
  onSyncComplete?: (results: SyncResult[]) => void;
}) => {
  const stringParentId = String(parentId);

  const {
    getPhotoObjectUrlsByParentId,
    cleanupObjectUrls,
    evidenceFiles: allEvidenceFiles,
    addEvidenceFile,
    removeEvidenceFile,
    getPhotoFromOfflineFile,
  } = useOfflineEvidenceStore();

  const evidenceFiles = useMemo(
    () => allEvidenceFiles.filter((file) => String(file.parentId) === stringParentId),
    [allEvidenceFiles, stringParentId]
  );

  const [offlinePhotos, setOfflinePhotos] = useState<OfflinePhoto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const loadingRef = useRef(false);

  const { mutateAsync: uploadEvidence, ...uploadRest } = useUploadEvidence(mode, mutationOptions);

  const loadOfflinePhotos = useCallback(async () => {
    if (loadingRef.current) {
      return;
    }

    loadingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const photos = await getPhotoObjectUrlsByParentId(stringParentId);
      logger.info('Loaded offline photos', { count: photos.length, parentId: stringParentId });
      setOfflinePhotos(photos);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to load offline photos');
      logger.error('Failed to load offline photos', { error });
      setError(error);
      setOfflinePhotos([]);
    } finally {
      setIsLoading(false);
      loadingRef.current = false;
    }
  }, [getPhotoObjectUrlsByParentId, stringParentId]);

  useEffect(() => {
    loadOfflinePhotos();

    return () => {
      cleanupObjectUrls(stringParentId);
    };
  }, [stringParentId, cleanupObjectUrls, loadOfflinePhotos]);

  useEffect(() => {
    if (!loadingRef.current) {
      // Add a small delay to ensure the store has been updated
      const timer = setTimeout(() => {
        loadOfflinePhotos();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [allEvidenceFiles.length, loadOfflinePhotos]);

  const uploadEvidenceWithOfflineSupport = useCallback(
    async (data: UploadEvidenceData) => {
      if (isOnline) {
        const exifData = await extractExifData(data.photo);
        return uploadEvidence({
          ...data,
          metadata: { ...data.metadata, taken_at: exifData.timestamp },
        });
      } else {
        logger.info('Device is offline, storing evidence for later upload', {
          evidenceType: data.evidenceType,
          fileName: data.photo.name,
          parentId: data.parentId,
        });

        const { onProgress, ...dataWithoutOnProgress } = data;

        const offlineId = await addEvidenceFile(dataWithoutOnProgress);

        // This helps prevent race conditions
        await new Promise((resolve) => setTimeout(resolve, 50));
        await loadOfflinePhotos();

        if (onProgress) {
          onProgress(100);
        }

        return {
          artifacts: [],
          created: new Date().toISOString(),
          id: offlineId,
          image: '',
          message: 'Stored offline',
          model_results: null,
          status: 'pending',
        } as OfflineProcessUploadResponse;
      }
    },
    [isOnline, uploadEvidence, addEvidenceFile, loadOfflinePhotos]
  );

  const syncOfflineEvidence = useCallback(async (): Promise<SyncComplete> => {
    if (!isOnline) {
      logger.warn('Cannot sync offline evidence while offline');
      return { message: 'Device is offline', results: [], success: false };
    }

    setIsSyncing(true);
    setError(null);

    const results: SyncResult[] = [];

    try {
      const filesToSync = evidenceFiles;
      if (filesToSync.length === 0) {
        setIsSyncing(false);
        return { message: 'No offline evidence to sync', results: [], success: true };
      }

      logger.info(`Syncing ${filesToSync.length} offline evidence files`, { parentId: stringParentId });

      await Promise.allSettled(
        filesToSync.map(async (offlineFile) => {
          try {
            const photo = await getPhotoFromOfflineFile(offlineFile);
            if (!photo) {
              throw new Error(`Failed to retrieve photo for offline file ${offlineFile.offlineId}`);
            }

            const uploadData: UploadEvidenceData = {
              evidenceType: offlineFile.evidenceType,
              metadata: offlineFile.metadata,
              model: offlineFile.model,
              parentId: offlineFile.parentId,
              photo,
              startTime: Date.now(),
            };

            const result = await uploadEvidence(uploadData);

            await removeEvidenceFile(offlineFile.offlineId);

            results.push({ offlineId: offlineFile.offlineId, result, success: true });
          } catch (err) {
            const error = err instanceof Error ? err : new Error('Failed to upload offline evidence');
            logger.error('Failed to upload offline evidence', {
              error,
              fileName: offlineFile.photoMeta.name,
              offlineId: offlineFile.offlineId,
            });
            results.push({ error, offlineId: offlineFile.offlineId, success: false });
          }
        })
      );

      const successful = results.filter((r) => r.success).length;
      const failed = results.filter((r) => !r.success).length;

      logger.info(`Synced ${successful} offline evidence files, ${failed} failed`, { parentId: stringParentId });

      await loadOfflinePhotos();

      return {
        message: `Synced ${successful} files${failed > 0 ? `, ${failed} failed` : ''}`,
        results,
        success: failed === 0,
      };
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to sync offline evidence');
      logger.error('Failed to sync offline evidence', { error });
      setError(error);
      return { message: error.message, results, success: false };
    } finally {
      setIsSyncing(false);
      onSyncComplete?.(results);
    }
  }, [
    isOnline,
    evidenceFiles,
    stringParentId,
    getPhotoFromOfflineFile,
    uploadEvidence,
    removeEvidenceFile,
    loadOfflinePhotos,
    onSyncComplete,
  ]);

  const clearOfflineEvidence = useCallback(async () => {
    const offlinePhotoIds = evidenceFiles.map((file) => file.offlineId);

    setIsClearing(true);
    setError(null);

    try {
      await Promise.all(offlinePhotoIds.map((id) => removeEvidenceFile(id)));
    } catch (error) {
      logger.error('Failed to clear offline evidence', { error });
      setError(error instanceof Error ? error : new Error('Failed to clear offline evidence'));
      throw error;
    } finally {
      setIsClearing(false);
    }
  }, [evidenceFiles, removeEvidenceFile]);

  return {
    clearOfflineEvidence,
    isClearing,
    isSyncing,
    mutateAsync: uploadEvidenceWithOfflineSupport,
    offlineError: error,
    offlineIsLoading: isLoading,
    offlinePhotos,
    refetchOfflinePhotos: loadOfflinePhotos,
    removeEvidenceFile,
    syncOfflineEvidence,
    ...uploadRest,
  };
};
